// Per-exchange recap drafter / approver UI — Wave A return-hooks.
//
// One route, four broad branches keyed off `(recap.state, viewer.role)`:
//   • no draft yet, exchange complete  → "Start a recap" CTA
//   • no draft yet, exchange not done  → "Recaps unlock once your stay is
//                                         complete" empty state
//   • drafted | declined (drafter)     → editor (body, title, photos, submit)
//   • drafted (other)                  → read-only "still drafting" view
//   • pending_approval (counterparty)  → approve / send-back-for-another-pass
//   • pending_approval (drafter)       → read-only "waiting on co-author" view
//   • published                        → permalink to /recaps/:slug
//
// CSRF: every mutation carries the wwaave_csrf cookie via csrfHeaders().
// API contract: see worker/routes/recaps.ts. Recaps live OUTSIDE the exchange
// thread and are public content — language matches that ("co-author", "story",
// "live recap") rather than the in-thread "review" vocabulary.

import {
  Alert,
  Anchor,
  Box,
  Button,
  Card,
  FileButton,
  Group,
  Loader,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import {
  RECAP_BODY_MAX as BODY_MAX,
  RECAP_DECLINE_MAX as DECLINE_MAX,
  RECAP_DECLINE_MIN as DECLINE_MIN,
  RECAP_PHOTO_MAX as PHOTO_MAX,
  RECAP_PHOTO_MAX_BYTES as PHOTO_MAX_BYTES,
  RECAP_TITLE_MAX as TITLE_MAX,
  type RecapDto,
  type RecapExchangeDto,
  type RecapMeResponse,
  type RecapPhotoDto,
  type RecapState,
} from '../../components/recap/types';
import { StateBadge } from '../../components/StateBadge';
import { EmptyState, MotionSection, PageHero } from '../../components/ui';
import { ApiError, fetchOk } from '../../lib/api';
import { csrfHeaders, useAuth } from '../../lib/auth-context';

export const Route = createFileRoute('/exchanges/$id_/recap')({
  component: RecapPage,
});

// ─────────────────────────────────────────────────────────────────────────────
// Page component
// ─────────────────────────────────────────────────────────────────────────────

function RecapPage() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const router = useRouter();

  const [data, setData] = useState<RecapMeResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadStatus, setLoadStatus] = useState<number | null>(null);

  const refresh = async (): Promise<void> => {
    try {
      const res = await fetchOk(`/api/recaps/me/exchange/${encodeURIComponent(id)}`, {
        credentials: 'include',
      });
      const body = (await res.json()) as RecapMeResponse;
      setData(body);
      setLoadError(null);
      setLoadStatus(null);
    } catch (e) {
      if (e instanceof ApiError) {
        setLoadError(e.message);
        setLoadStatus(e.status);
      } else {
        setLoadError('Network problem loading the recap.');
        setLoadStatus(null);
      }
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loadError !== null) {
    const friendly =
      loadStatus === 403
        ? 'You are not on this exchange, so the recap is not visible to you.'
        : loadStatus === 404
          ? "We couldn't find that exchange."
          : loadError;
    return (
      <AppShell>
        <Stack gap="md">
          <Alert color="red">{friendly}</Alert>
          <Anchor component={Link} to="/exchanges" size="sm">
            ← Back to your exchanges
          </Anchor>
        </Stack>
      </AppShell>
    );
  }

  if (data === null) {
    return (
      <AppShell>
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </AppShell>
    );
  }

  const meId = user?.id ?? null;
  const recap = data.recap;
  const photos = data.photos;

  const startsLabel = data.exchange.start_date;
  const endsLabel = data.exchange.end_date;
  const dateLine = `${startsLabel} → ${endsLabel}`;

  return (
    <AppShell>
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Exchange recap"
            title={data.exchange.venue_name}
            lede={dateLine}
            action={
              recap !== null ? (
                <StateBadge entity="exchange_recap" state={recap.state} size="lg" />
              ) : undefined
            }
          />
        </MotionSection>

        <MotionSection>
          {/* Mantine + TanStack polymorphism gotcha (CLAUDE.md): wrap the
              typed <Link> around a presentational <Anchor component="span">
              so the Link owns navigation + typed params and the Anchor owns
              visuals. */}
          <Link
            to="/exchanges/$id"
            params={{ id }}
            style={{ textDecoration: 'none' }}
          >
            <Anchor component="span" size="sm">
              ← Back to the exchange
            </Anchor>
          </Link>
        </MotionSection>

        <MotionSection>
          <RecapBody
            data={data}
            recap={recap}
            photos={photos}
            meId={meId}
            exchangeId={id}
            refresh={refresh}
            onPublishedNavigateHome={() => {
              // After publish we don't auto-navigate — the "View live recap"
              // link surfaces in the published branch and the user can move at
              // their own pace. Keeping the router invalidate so any list
              // route they pop back to refetches.
              router.invalidate();
            }}
          />
        </MotionSection>
      </Stack>
    </AppShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Body branching
// ─────────────────────────────────────────────────────────────────────────────

function RecapBody(props: {
  data: RecapMeResponse;
  recap: RecapDto | null;
  photos: RecapPhotoDto[];
  meId: string | null;
  exchangeId: string;
  refresh: () => Promise<void>;
  onPublishedNavigateHome: () => void;
}) {
  const { data, recap, photos, meId, exchangeId, refresh, onPublishedNavigateHome } = props;

  // Branch 1 — no draft and not yet eligible.
  if (recap === null && !data.eligible) {
    return (
      <EmptyState
        title="Recaps unlock once your exchange is complete."
        body="Mark the stay complete from the exchange page and the recap composer opens here."
        action={
          <Link to="/exchanges/$id" params={{ id: exchangeId }} style={{ textDecoration: 'none' }}>
            <Anchor component="span">Go to the exchange</Anchor>
          </Link>
        }
      />
    );
  }

  // Branch 2 — eligible but no draft yet.
  if (recap === null && data.eligible) {
    return (
      <StartRecapCta
        exchangeId={exchangeId}
        venueName={data.exchange.venue_name}
        refresh={refresh}
      />
    );
  }

  // recap is non-null past this point.
  if (recap === null) return null;

  const iAmDrafter = meId !== null && recap.drafted_by_user_id === meId;
  const iAmCounterparty = meId !== null && recap.counterparty_user_id === meId;

  // Branch 3 — published. Permalink to /recaps/:slug.
  if (recap.state === 'published') {
    return (
      <PublishedView
        recap={recap}
        photos={photos}
        onAfter={onPublishedNavigateHome}
      />
    );
  }

  // Branch 4 — drafted, drafter is current user.
  if (recap.state === 'drafted' && iAmDrafter) {
    return (
      <DrafterEditor
        recap={recap}
        photos={photos}
        exchangeId={exchangeId}
        refresh={refresh}
      />
    );
  }

  // Branch 5 — drafted, but the OTHER party is the drafter.
  if (recap.state === 'drafted' && !iAmDrafter) {
    return (
      <ReadOnlyView
        banner={{
          color: 'blue',
          message: 'Your co-author is still drafting. They will send it your way to confirm.',
        }}
        recap={recap}
        photos={photos}
      />
    );
  }

  // Branch 6 — pending_approval, counterparty is current user.
  if (recap.state === 'pending_approval' && iAmCounterparty) {
    return (
      <ApproverView
        recap={recap}
        photos={photos}
        exchangeId={exchangeId}
        refresh={refresh}
      />
    );
  }

  // Branch 7 — pending_approval, drafter is current user.
  if (recap.state === 'pending_approval' && iAmDrafter) {
    return (
      <ReadOnlyView
        banner={{
          color: 'yellow',
          message: 'Sent for approval. Waiting on your co-author to take a look.',
        }}
        recap={recap}
        photos={photos}
      />
    );
  }

  // Branch 8 — declined, drafter is current user (revise + edit).
  if (recap.state === 'declined' && iAmDrafter) {
    return (
      <DrafterEditor
        recap={recap}
        photos={photos}
        exchangeId={exchangeId}
        refresh={refresh}
        declinedReason={recap.declined_reason}
      />
    );
  }

  // Branch 9 — declined, counterparty is current user. Read-only with the
  // reason they sent so they remember what they asked for.
  if (recap.state === 'declined' && iAmCounterparty) {
    return (
      <ReadOnlyView
        banner={{
          color: 'sun',
          message:
            'Sent back for another pass. Your co-author will revise and resubmit.',
        }}
        recap={recap}
        photos={photos}
        showDeclineReason
      />
    );
  }

  // Defensive fallthrough — we either don't recognise the viewer or the state
  // shifted under us. Shouldn't hit in normal operation.
  return (
    <Alert color="gray" variant="light">
      This recap is not currently editable from your account.
    </Alert>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Branch components
// ─────────────────────────────────────────────────────────────────────────────

function StartRecapCta(props: {
  exchangeId: string;
  venueName: string;
  refresh: () => Promise<void>;
}) {
  const { exchangeId, venueName, refresh } = props;
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  async function start(): Promise<void> {
    setBusy(true);
    setErrMsg(null);
    try {
      await fetchOk(`/api/recaps/me/exchange/${encodeURIComponent(exchangeId)}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...csrfHeaders(),
        },
        body: JSON.stringify({ exchange_id: exchangeId, body: '' }),
      });
      await refresh();
    } catch (e) {
      setErrMsg(e instanceof ApiError ? e.message : 'Could not start a draft just now.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={3}>Start a recap</Title>
          <Text c="dimmed">
            Co-author a public recap of your night at {venueName}. Either of you can
            draft it; the other confirms before it goes live on your profiles and the
            stories grid.
          </Text>
        </Stack>
        {errMsg !== null ? <Alert color="red">{errMsg}</Alert> : null}
        <Group>
          <Button onClick={start} loading={busy}>
            Start a recap
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

function DrafterEditor(props: {
  recap: RecapDto;
  photos: RecapPhotoDto[];
  exchangeId: string;
  refresh: () => Promise<void>;
  declinedReason?: string | null;
}) {
  const { recap, photos, exchangeId, refresh, declinedReason } = props;

  const [body, setBody] = useState<string>(recap.body);
  const [title, setTitle] = useState<string>(recap.title ?? '');
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  async function saveDraft(): Promise<void> {
    setSavingDraft(true);
    setEditError(null);
    try {
      const payload: { exchange_id: string; body: string; title?: string } = {
        exchange_id: exchangeId,
        body,
      };
      const trimmedTitle = title.trim();
      if (trimmedTitle.length > 0) payload.title = trimmedTitle;
      await fetchOk(`/api/recaps/me/exchange/${encodeURIComponent(exchangeId)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify(payload),
      });
      notifications.show({ message: 'Saved.', color: 'teal' });
      await refresh();
    } catch (e) {
      setEditError(e instanceof ApiError ? e.message : "Couldn't save just now.");
    } finally {
      setSavingDraft(false);
    }
  }

  async function submitForApproval(): Promise<void> {
    setSubmitting(true);
    setEditError(null);
    try {
      // If we're revising a declined recap, the SM requires drafter.revise
      // first to flip declined → drafted. We chain it ahead of submit so the
      // flow stays one click from the user's perspective.
      if (recap.state === 'declined') {
        await fetchOk(
          `/api/recaps/me/exchange/${encodeURIComponent(exchangeId)}/revise`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
            body: JSON.stringify({}),
          },
        );
      }
      // Persist the latest edits before kicking submit.
      const payload: { exchange_id: string; body: string; title?: string } = {
        exchange_id: exchangeId,
        body,
      };
      const trimmedTitle = title.trim();
      if (trimmedTitle.length > 0) payload.title = trimmedTitle;
      await fetchOk(`/api/recaps/me/exchange/${encodeURIComponent(exchangeId)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify(payload),
      });
      await fetchOk(
        `/api/recaps/me/exchange/${encodeURIComponent(exchangeId)}/submit`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
          body: JSON.stringify({ exchange_id: exchangeId }),
        },
      );
      notifications.show({
        message: 'Sent to your co-author for approval.',
        color: 'teal',
      });
      await refresh();
    } catch (e) {
      setEditError(
        e instanceof ApiError ? e.message : "Couldn't send for approval just now.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const bodyTrimmed = body.trim().length;
  const canSubmit = bodyTrimmed >= 1 && bodyTrimmed <= BODY_MAX && !submitting;

  return (
    <Stack gap="lg">
      {declinedReason !== null && declinedReason !== undefined && declinedReason.length > 0 ? (
        <Alert color="sun" variant="light" title="Sent back for another pass">
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {declinedReason}
          </Text>
          <Text size="xs" c="dimmed" mt="xs">
            Edit below and resubmit when you&apos;re ready.
          </Text>
        </Alert>
      ) : null}

      <Card withBorder radius="md" padding="lg">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>Draft</Title>
            <Text size="sm" c="dimmed">
              Write the recap as you&apos;d want a stranger to read it. What the night
              felt like, what surprised you, who showed up. Your co-author will
              confirm before it goes live.
            </Text>
          </Stack>

          <TextInput
            label="Title (optional)"
            placeholder="A line that captures the night."
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            maxLength={TITLE_MAX}
            description={`${title.length} / ${TITLE_MAX}`}
          />

          <Textarea
            label="Recap"
            placeholder="What happened, in your words."
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            minRows={8}
            autosize
            maxLength={BODY_MAX}
            description={`${body.length} / ${BODY_MAX}`}
          />

          {editError !== null ? <Alert color="red">{editError}</Alert> : null}

          <Group justify="space-between">
            <Button
              variant="default"
              onClick={saveDraft}
              loading={savingDraft}
              disabled={submitting}
            >
              Save draft
            </Button>
            <Button onClick={submitForApproval} loading={submitting} disabled={!canSubmit}>
              Submit for approval
            </Button>
          </Group>
        </Stack>
      </Card>

      <PhotoManager
        recapId={recap.id}
        photos={photos}
        exchangeId={exchangeId}
        refresh={refresh}
      />
    </Stack>
  );
}

function ApproverView(props: {
  recap: RecapDto;
  photos: RecapPhotoDto[];
  exchangeId: string;
  refresh: () => Promise<void>;
}) {
  const { recap, photos, exchangeId, refresh } = props;
  const [approving, setApproving] = useState(false);
  const [decliningOpen, { open: openDecline, close: closeDecline }] = useDisclosure(false);
  const [reason, setReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function approve(): Promise<void> {
    setApproving(true);
    setActionError(null);
    try {
      await fetchOk(
        `/api/recaps/me/exchange/${encodeURIComponent(exchangeId)}/approve`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
          body: JSON.stringify({}),
        },
      );
      notifications.show({ message: 'Recap published.', color: 'teal' });
      await refresh();
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : "Couldn't publish just now.");
    } finally {
      setApproving(false);
    }
  }

  async function decline(): Promise<void> {
    const trimmed = reason.trim();
    if (trimmed.length < DECLINE_MIN) {
      setActionError(
        `Leave a short note (at least ${DECLINE_MIN} characters) so your co-author knows what to revise.`,
      );
      return;
    }
    setDeclining(true);
    setActionError(null);
    try {
      await fetchOk(
        `/api/recaps/me/exchange/${encodeURIComponent(exchangeId)}/decline`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
          body: JSON.stringify({ exchange_id: exchangeId, reason: trimmed }),
        },
      );
      notifications.show({
        message: 'Sent back to your co-author with your note.',
        color: 'teal',
      });
      closeDecline();
      setReason('');
      await refresh();
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : "Couldn't send back just now.");
    } finally {
      setDeclining(false);
    }
  }

  return (
    <Stack gap="lg">
      <Alert color="yellow" variant="light">
        Your co-author drafted this. Read it through, then publish or send
        it back with a note.
      </Alert>

      <RecapReadCard recap={recap} photos={photos} />

      {actionError !== null ? <Alert color="red">{actionError}</Alert> : null}

      <Group>
        <Button color="teal" onClick={approve} loading={approving}>
          Publish
        </Button>
        <Button variant="default" onClick={openDecline}>
          Send back for another pass
        </Button>
      </Group>

      <Modal
        opened={decliningOpen}
        onClose={closeDecline}
        title="Send back for another pass"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            Tell your co-author what to revise. They&apos;ll see this note in their
            editor.
          </Text>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            placeholder="Could you add the part about the encore?"
            minRows={4}
            autosize
            maxLength={DECLINE_MAX}
            description={`${reason.length} / ${DECLINE_MAX}`}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeDecline} disabled={declining}>
              Go back
            </Button>
            <Button
              color="sun"
              onClick={decline}
              loading={declining}
              disabled={reason.trim().length < DECLINE_MIN}
            >
              Send back
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function ReadOnlyView(props: {
  banner: { color: string; message: string };
  recap: RecapDto;
  photos: RecapPhotoDto[];
  showDeclineReason?: boolean;
}) {
  const { banner, recap, photos, showDeclineReason } = props;
  return (
    <Stack gap="lg">
      <Alert color={banner.color} variant="light">
        {banner.message}
      </Alert>
      {showDeclineReason && recap.declined_reason !== null ? (
        <Alert color="sun" variant="light" title="The note you sent">
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {recap.declined_reason}
          </Text>
        </Alert>
      ) : null}
      <RecapReadCard recap={recap} photos={photos} />
    </Stack>
  );
}

function PublishedView(props: {
  recap: RecapDto;
  photos: RecapPhotoDto[];
  onAfter: () => void;
}) {
  const { recap, photos, onAfter } = props;

  async function copyLink(): Promise<void> {
    if (recap.slug === null) return;
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
    try {
      const url = `${window.location.origin}/recaps/${recap.slug}`;
      await navigator.clipboard.writeText(url);
      notifications.show({ message: 'Link copied to your clipboard.', color: 'teal' });
    } catch {
      notifications.show({
        message: "Couldn't copy automatically. Try selecting the link manually.",
        color: 'red',
      });
    }
  }

  const canCopy =
    typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function';

  return (
    <Stack gap="lg">
      <Alert color="teal" variant="light" title="Live">
        Your recap is published and visible on your profile, your co-author&apos;s profile,
        and the stories grid.
      </Alert>

      <RecapReadCard recap={recap} photos={photos} />

      <Group>
        {recap.slug !== null ? (
          <Link
            to="/recaps/$slug"
            params={{ slug: recap.slug }}
            onClick={onAfter}
            style={{ textDecoration: 'none' }}
          >
            <Button component="span" tabIndex={-1}>
              View live recap
            </Button>
          </Link>
        ) : null}
        {canCopy ? (
          <Button variant="default" onClick={copyLink}>
            Copy link
          </Button>
        ) : null}
      </Group>
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared sub-components
// ─────────────────────────────────────────────────────────────────────────────

function RecapReadCard(props: { recap: RecapDto; photos: RecapPhotoDto[] }) {
  const { recap, photos } = props;
  const sortedPhotos = [...photos].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack gap="md">
        {recap.title !== null && recap.title.length > 0 ? (
          <Title order={3}>{recap.title}</Title>
        ) : null}
        <Text style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
          {recap.body.length > 0 ? recap.body : <Text c="dimmed">(empty)</Text>}
        </Text>
        {sortedPhotos.length > 0 ? (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            {sortedPhotos.map((p, idx) => (
              <Box
                key={p.id}
                style={{
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: 'var(--mantine-color-brand-0)',
                }}
              >
                <img
                  src={p.url}
                  alt={p.caption ?? `Recap photo ${idx + 1}`}
                  loading="lazy"
                  decoding="async"
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover',
                  }}
                />
                {p.caption !== null && p.caption.length > 0 ? (
                  <Text size="xs" c="dimmed" px="sm" py={6}>
                    {p.caption}
                  </Text>
                ) : null}
              </Box>
            ))}
          </SimpleGrid>
        ) : null}
      </Stack>
    </Card>
  );
}

function PhotoManager(props: {
  recapId: string;
  photos: RecapPhotoDto[];
  exchangeId: string;
  refresh: () => Promise<void>;
}) {
  const { photos, exchangeId, refresh } = props;
  const sortedPhotos = [...photos].sort((a, b) => a.sort_order - b.sort_order);
  const remaining = Math.max(0, PHOTO_MAX - sortedPhotos.length);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const resetRef = useRef<() => void>(null);

  async function onPick(file: File | null): Promise<void> {
    if (file === null) return;
    if (file.size > PHOTO_MAX_BYTES) {
      setUploadError('Photos must be 8 MB or smaller.');
      resetRef.current?.();
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      await fetchOk(
        `/api/recaps/me/exchange/${encodeURIComponent(exchangeId)}/photos`,
        {
          method: 'POST',
          credentials: 'include',
          // Do not set Content-Type — the browser fills the boundary param.
          headers: { ...csrfHeaders() },
          body: fd,
        },
      );
      await refresh();
    } catch (e) {
      setUploadError(e instanceof ApiError ? e.message : "Couldn't upload that photo.");
    } finally {
      setUploading(false);
      resetRef.current?.();
    }
  }

  async function deletePhoto(photoId: string): Promise<void> {
    try {
      await fetchOk(
        `/api/recaps/me/exchange/${encodeURIComponent(exchangeId)}/photos/${encodeURIComponent(photoId)}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: { ...csrfHeaders() },
        },
      );
      await refresh();
    } catch (e) {
      notifications.show({
        message: e instanceof ApiError ? e.message : "Couldn't remove that photo.",
        color: 'red',
      });
    }
  }

  async function updateCaption(photoId: string, nextCaption: string): Promise<void> {
    try {
      await fetchOk(
        `/api/recaps/me/exchange/${encodeURIComponent(exchangeId)}/photos/${encodeURIComponent(photoId)}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
          body: JSON.stringify({ caption: nextCaption }),
        },
      );
      await refresh();
    } catch (e) {
      notifications.show({
        message: e instanceof ApiError ? e.message : "Couldn't save the caption.",
        color: 'red',
      });
    }
  }

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={4}>Photos</Title>
          <Text size="sm" c="dimmed">
            Up to {PHOTO_MAX} photos. JPEG / PNG / WebP, 8 MB each. Captions optional.
          </Text>
        </Stack>

        <Group gap="xs" wrap="wrap">
          <FileButton resetRef={resetRef} accept="image/*" onChange={onPick}>
            {(btnProps) => (
              <Button
                {...btnProps}
                variant="default"
                loading={uploading}
                disabled={remaining === 0 || uploading}
              >
                {remaining === 0 ? 'Photo limit reached' : 'Add a photo'}
              </Button>
            )}
          </FileButton>
          <Text size="sm" c="dimmed">
            {sortedPhotos.length} / {PHOTO_MAX}
          </Text>
        </Group>

        {uploadError !== null ? <Alert color="red">{uploadError}</Alert> : null}

        {sortedPhotos.length === 0 ? (
          <Text size="sm" c="dimmed">
            No photos yet. They&apos;re optional, but they make the recap land.
          </Text>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            {sortedPhotos.map((p, idx) => (
              <PhotoEditorTile
                key={p.id}
                photo={p}
                index={idx}
                onDelete={() => deletePhoto(p.id)}
                onCaptionSave={(next) => updateCaption(p.id, next)}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Card>
  );
}

function PhotoEditorTile(props: {
  photo: RecapPhotoDto;
  index: number;
  onDelete: () => Promise<void>;
  onCaptionSave: (next: string) => Promise<void>;
}) {
  const { photo, index, onDelete, onCaptionSave } = props;
  const [caption, setCaption] = useState<string>(photo.caption ?? '');
  const [savingCaption, setSavingCaption] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const dirty = caption !== (photo.caption ?? '');

  return (
    <Box
      style={{
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid var(--mantine-color-default-border)',
        background: 'var(--mantine-color-body)',
      }}
    >
      <img
        src={photo.url}
        alt={photo.caption ?? `Recap photo ${index + 1}`}
        loading="lazy"
        decoding="async"
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
          objectFit: 'cover',
          aspectRatio: '4 / 3',
        }}
      />
      <Stack gap="xs" p="sm">
        <TextInput
          placeholder="Caption (optional)"
          value={caption}
          onChange={(e) => setCaption(e.currentTarget.value)}
          maxLength={200}
          size="xs"
        />
        <Group justify="space-between">
          <Button
            size="xs"
            variant="default"
            disabled={!dirty || savingCaption}
            loading={savingCaption}
            onClick={async () => {
              setSavingCaption(true);
              try {
                await onCaptionSave(caption);
              } finally {
                setSavingCaption(false);
              }
            }}
          >
            Save caption
          </Button>
          <Button
            size="xs"
            variant="subtle"
            color="red"
            loading={deleting}
            onClick={async () => {
              setDeleting(true);
              try {
                await onDelete();
              } finally {
                setDeleting(false);
              }
            }}
          >
            Remove
          </Button>
        </Group>
      </Stack>
    </Box>
  );
}

